/* eslint-disable simple-import-sort/imports */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axios from "../../../../utils/api-client";
import { setSupervisorRateLimitError } from "../../../user/user-slice";
import { RegularShiftRequest } from "../../store/worker-requests-types";
import {
	CallLogsStateTypes,
	FetchCallLogsProps,
	FetchLinkedRequestsProps,
} from "./call-logs-types";

const initialState: CallLogsStateTypes = {
	callLogs: [],
	fetchCallLogs: {
		status: "idle",
		errorMessage: "",
	},
	linkedRequests: {
		status: "idle",
		errorMessage: "",
		data: [],
		forConversationId: null,
	},
};

// Fetches every call-off line call for the supervisor's date range
export const fetchCallLogs = createAsyncThunk(
	"call-logs/fetch",
	async (
		{ startDate, endDate }: FetchCallLogsProps,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const queryParams = new URLSearchParams({ startDate, endDate });
			const res = await axios.get(
				`${process.env.REACT_APP_API_END_POINT}/workers/callLogs?${queryParams.toString()}`
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue("An error occurred");
			}
		}
	}
);

// Loads the full request rows a call produced, keyed off linkedRequestIds
export const fetchLinkedRequests = createAsyncThunk(
	"call-logs/fetch-linked-requests",
	async (
		{ requestIds }: FetchLinkedRequestsProps,
		{ fulfillWithValue, rejectWithValue, dispatch }
	) => {
		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_END_POINT}/workers/requestsForUser/byIds`,
				{ ids: requestIds }
			);
			return fulfillWithValue(res.data);
		} catch (error: any) {
			const errorResponse = error?.response?.data;
			errorResponse.status = error?.response?.status;
			try {
				if (errorResponse.status === 429) {
					dispatch(setSupervisorRateLimitError());
					return rejectWithValue(errorResponse);
				}
				return rejectWithValue(error.response.data.message);
			} catch (e) {
				return rejectWithValue("An error occurred");
			}
		}
	}
);

export const CallLogsSlice = createSlice({
	name: "callLogs",
	initialState,
	reducers: {
		resetLinkedRequests: (state) => {
			state.linkedRequests = {
				status: "idle",
				errorMessage: "",
				data: [],
				forConversationId: null,
			};
		},
	},
	extraReducers: (builder) => {
		// fetchCallLogs
		builder.addCase(fetchCallLogs.pending, (state) => {
			state.fetchCallLogs.status = "pending";
		});
		builder.addCase(fetchCallLogs.rejected, (state, action) => {
			state.fetchCallLogs.status = "error";
			if ((action.payload as { status?: number })?.status !== 429) {
				state.fetchCallLogs.errorMessage =
					(action.payload as string | object) ?? "An error occurred";
			}
		});
		builder.addCase(fetchCallLogs.fulfilled, (state, action) => {
			state.callLogs = action.payload ?? [];
			state.fetchCallLogs.status = "fulfilled";
		});

		// fetchLinkedRequests
		builder.addCase(fetchLinkedRequests.pending, (state, action) => {
			state.linkedRequests.status = "pending";
			state.linkedRequests.forConversationId =
				action.meta.arg.conversationId;
		});
		builder.addCase(fetchLinkedRequests.rejected, (state, action) => {
			state.linkedRequests.status = "error";
			if ((action.payload as { status?: number })?.status !== 429) {
				state.linkedRequests.errorMessage =
					(action.payload as string | object) ?? "An error occurred";
			}
		});
		builder.addCase(fetchLinkedRequests.fulfilled, (state, action) => {
			state.linkedRequests.status = "fulfilled";
			state.linkedRequests.data =
				(action.payload as RegularShiftRequest[]) ?? [];
		});
	},
});

export const { resetLinkedRequests } = CallLogsSlice.actions;

export default CallLogsSlice.reducer;
